import React, { useState } from 'react';
import ChatContainer from './ChatContainer';
import { ChatProvider } from '../context/ChatContext';
import { ChatWidgetConfig } from '../types';
import { useChatContext } from '../context/ChatContext';

export type ChatWidgetProps = ChatWidgetConfig & {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onStateChange?: (isOpen: boolean) => void;
};

// Inner component that has access to the chat context
const ChatWidgetContent: React.FC<Omit<ChatWidgetProps, 'clientId' | 'assistantId' | 'region'>> = ({
  // Widget state
  isOpen: externalIsOpen,
  onOpen,
  onClose,
  onStateChange,
  defaultOpen = false,
  
  // Widget appearance - these will be overridden by server config when available
  position = 'bottom-right',
  triggerButtonLabel = 'Chat with us',
  assistantName = 'John',
  assistantAvatar,
  welcomeMessage = "We're here to help you manage your debt. Any information you share will be used to assist you on your financial journey.",
  suggestedPrompts = [],
  primaryColor = '#3b82f6',
  
  // Display options
  embedded = false,
  height = '600px',
  width = '380px',
  
}) => {
  // Get the server config from context
  const { serverConfig } = useChatContext();
  
  // Manage internal open state if not controlled externally
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  
  const handleToggle = (newState: boolean) => {
    if (externalIsOpen === undefined) {
      setInternalIsOpen(newState);
    }
    
    if (newState) {
      onOpen?.();
    } else {
      onClose?.();
    }
    
    onStateChange?.(newState);
  };
  
  // Apply server config values if available
  const effectiveAssistantName = serverConfig?.chatbot_name || assistantName;
  const effectiveWelcomeMessage = serverConfig?.header_content || welcomeMessage;
  const effectivePrimaryColor = serverConfig?.primary_color || primaryColor;
  
  // Calculate position CSS class
  const positionStyles = {
    'bottom-right': 'skit-ai-llm-bottom-4 skit-ai-llm-right-4',
    'bottom-left': 'skit-ai-llm-bottom-4 skit-ai-llm-left-4',
    'top-right': 'skit-ai-llm-top-4 skit-ai-llm-right-4',
    'top-left': 'skit-ai-llm-top-4 skit-ai-llm-left-4',
  };
  
  if (embedded) {
    return (
      <div className="skit-ai-llm-h-full skit-ai-llm-w-full">
        <ChatContainer
          assistantName={effectiveAssistantName}
          assistantAvatar={assistantAvatar}
          welcomeMessage={effectiveWelcomeMessage}
          suggestedPrompts={suggestedPrompts}
          containerClassName="skit-ai-llm-h-full"
          showCloseButton={false}
          primaryColor={effectivePrimaryColor}
        />
      </div>
    );
  }
  
  return (
    <div className="llm-studio-chat-widget">
      {isOpen ? (
        <div 
          className={`skit-ai-llm-fixed ${positionStyles[position]} skit-ai-llm-z-50 skit-ai-llm-overflow-hidden skit-ai-llm-transition-all skit-ai-llm-duration-300`}
          style={{ height, width, zIndex: 9999 }}
        >
          <ChatContainer
            assistantName={effectiveAssistantName}
            assistantAvatar={assistantAvatar}
            welcomeMessage={effectiveWelcomeMessage}
            suggestedPrompts={suggestedPrompts}
            onClose={() => handleToggle(false)}
            showCloseButton={true}
            containerClassName="skit-ai-llm-h-full"
            primaryColor={effectivePrimaryColor}
          />
        </div>
      ) : (
        <button
          onClick={() => handleToggle(true)}
          className={`skit-ai-llm-fixed ${positionStyles[position]} skit-ai-llm-z-50 skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-rounded-full skit-ai-llm-px-4 skit-ai-llm-py-3 skit-ai-llm-text-white skit-ai-llm-shadow-lg skit-ai-llm-transition-colors hover:skit-ai-llm-bg-opacity-90`}
          style={{ 
            backgroundColor: effectivePrimaryColor, 
            zIndex: 9999,
            position: 'fixed',
            bottom: position.includes('bottom') ? '16px' : 'auto',
            top: position.includes('top') ? '16px' : 'auto',
            right: position.includes('right') ? '16px' : 'auto',
            left: position.includes('left') ? '16px' : 'auto',
            display: 'flex',
            alignItems: 'center',
            visibility: 'visible'
          }}
        >
          <span className="skit-ai-llm-mr-2">{triggerButtonLabel}</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

// Main ChatWidget component that provides the ChatContext
const ChatWidget: React.FC<ChatWidgetProps> = ({
  // Required configuration for the API
  clientId,
  assistantId,
  region,
  
  // Pass all other props to the inner component
  ...restProps
}) => {
  // Configuration for the ChatProvider
  const config: ChatWidgetConfig = {
    clientId,
    assistantId,
    region,
    userId: restProps.userId,
    autoSendFirstMessage: restProps.autoSendFirstMessage !== false, 
    primaryColor: restProps.primaryColor,
  };
  
  return (
    <ChatProvider config={config}>
      <ChatWidgetContent {...restProps} />
    </ChatProvider>
  );
};

export default ChatWidget; 